<template>
    <Dialog
        :value="showDialog"
        :header-text="title"
        persistent
        @close_modal="$emit('close')"
    >
        <div v-if="formData">
            <v-row>
                <v-col class="col-12 pt-0">
                    <v-text-field
                        v-model="formData.name"
                        :label="$t('Name')"
                        :error-messages="formData.name_error"
                        :hide-details="!formData.name_error"
                        :disabled="isSysGroup"
                        outlined
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1 d-flex align-center mt-4">
                    <span class="mr-2">{{ $t("Members") }}</span>
                    <dropdown
                        v-if="!isSysGroup"
                        :items="selectablePeople"
                        name-key="name"
                        value-key="id"
                        :disabled="disableEditing"
                        lazy
                        multi
                        deselect
                        @confirm="addMembersClick"
                    >
                        <template #default="{ on }">
                            <span v-on="on">
                                <action-button>
                                    {{ $t("Add") }}
                                </action-button>
                            </span>
                        </template>
                    </dropdown>
                </v-col>
            </v-row>
            <v-row v-for="account_member in selectedPeople" :key="account_member.id">
                <v-col class="py-2">
                    <AccountChip
                        :account="account_member"
                        :show-is-primary="false"
                        :base-url="baseUrl"
                        :closable="!isSysGroup"
                        @remove="removeMemberClick(account_member.id)"
                    ></AccountChip>
                </v-col>
            </v-row>
            <v-row>
                <v-col v-if="formData.person_ids.length === 0">
                    {{ $t('No members yet') }}
                </v-col>
            </v-row>
        </div>
        <template slot="footer">
            <v-btn v-if="formData && formData.id && !isSysGroup" icon text color="grey" @click="onDeleteClick">
                <icon
                    :size="24"
                    class="mr-0"
                    icon="Trash2"
                />
            </v-btn>
            <v-spacer />
            <v-btn v-if="!isSysGroup" text @click="$emit('close')">
                {{ $t("Cancel") }}
            </v-btn>
            <v-btn v-if="!isSysGroup" class="primary inverted--text" :loading="saveLoading" @click="saveGroup">
                {{ $t("Save") }}
            </v-btn>
        </template>
    </Dialog>
</template>

<script>
import Vue from "vue";
import Dialog from "@/components/Dialog";
import { ref, computed } from "@vue/composition-api";
import { $t } from "@/i18n";
import AppModule from "@/store/modules/app";
import UserModule from "@/store/modules/user";
import AccountChip from "../../components/AccountChip";
import DialogModule from "@/store/modules/dialog";
import ComboboxDropdown from "../../components/ComboboxDropdown";
import errorDialog from "@/views/ErrorDialog/errorDialog"

export default {
    name: "GroupEditDialog",
    components: {
        Dialog,
        AccountChip,
        ComboboxDropdown
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true
        },
        accountId: {
            type: Number,
            required: true
        },
        groupId: {
            type: Number,
            default: null
        }
    },
    setup(props, ctx) {
        const { handleErrorResponse, clearValidationErrors, baseUrl, refreshAccountsInfo, clearClassifiersCache } = AppModule();
        const { fetchAccountGroup, saveAccountGroup, deleteAccountGroup, loadMembers, getMembersByIds, getVisiblePeopleByCompany} = UserModule();
        const { openConfirmDialog, setConfirmOptions } = DialogModule();

        const formData = ref(null);
        const isSysGroup = ref(false);
        const disableEditing = ref(false);
        const saveLoading = ref(false);

        const title = computed(() => {
            return (props.groupId) ? $t("Edit group") : $t("Add group");
        });

        const groupStruct = {
            name: "text",
            person_ids: "list"
        };

        const selectablePeople = computed(() => {
            const people = getVisiblePeopleByCompany(props.accountId)
            return people.filter(x => !formData.value.person_ids.includes(x.id))
        })

        const selectedPeople = computed(() => {
            return getMembersByIds(formData.value.person_ids)
        })

        const loadData = async () => {
            if (!props.groupId) {
                isSysGroup.value = false;
                disableEditing.value = false;
                formData.value = Vue.prepare_form_data(
                    { id: 0, account_id: props.accountId, /*role: 'no_role',*/ person_ids: []},
                    groupStruct
                );
            }
            else {
                fetchAccountGroup(props.groupId)
                    .then(resp => {
                        if (resp.data != null && resp.data.group_type !== undefined) {
                            isSysGroup.value = true;
                            disableEditing.value = (resp.data['group_type'] === "all_users");
                        }
                        formData.value = Vue.prepare_form_data(resp, groupStruct);
                    })
                    .catch(ex => errorDialog.showError(ex));
            }
            try {
                await loadMembers()
            } catch (e) {
                errorDialog.showError(e)
            }
        };
        const addMembersClick = (e) => {
            const personIds = e.map(x => x.id)
            formData.value.person_ids = formData.value.person_ids.concat(personIds)
        };
        const removeMemberClick = (id) => {
            formData.value.person_ids = formData.value.person_ids.filter(x => x !== id)
        };
        const saveGroup = () => {
            saveLoading.value = true;
            clearValidationErrors(formData.value);
            saveAccountGroup(formData.value)
                .then(resp => {
                    ctx.emit("update", resp);
                    ctx.emit("close");
                    clearClassifiersCache('account_groups')
                    refreshAccountsInfo();
                    loadMembers();
                })
                .catch(ex => {
                    handleErrorResponse(formData.value, ex);
                    saveLoading.value = false;
                });
        };
        const onDeleteClick = () => {
            setConfirmOptions({
                title: $t("Are you sure to delete this user group?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => deleteGroup()
            });
            openConfirmDialog();
        };
        const deleteGroup = () => {
            deleteAccountGroup(formData.value.id)
                .then(resp => {
                    ctx.emit("deleted", formData.value.id);
                    ctx.emit("close");
                    clearClassifiersCache('account_groups')
                })
                .catch(ex => {
                    errorDialog.showError(ex);
                });
        };

        loadData();

        return {
            title,
            formData,
            isSysGroup,
            saveLoading,
            baseUrl,
            disableEditing,
            addMembersClick,
            removeMemberClick,
            saveGroup,
            onDeleteClick,
            getVisiblePeopleByCompany,
            selectablePeople,
            selectedPeople
        };
    }
}
</script>
